import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import DoubleHappiness from './DoubleHappiness';

const RSVPForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    guests: 1,
    phone: '',
    email: '',
    attending: true,
    dietary_restrictions: '',
    message: ''
  });

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'radio' ? value === 'true' : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post('/api/rsvp', formData);
      if (response.data.success) {
        // 将RSVP数据传递到感谢页面
        navigate('/thank-you', { 
          state: { 
            rsvpData: formData,
            submissionId: response.data.id 
          } 
        });
      }
    } catch (error) {
      alert('提交失败，请稍后重试 Submission failed, please try again');
      console.error('Error submitting RSVP:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50 to-red-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center text-chinese-red hover:text-chinese-dark mb-6 transition-colors"
        >
          <span className="mr-2">←</span> 返回主页 Back to Home
        </Link>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-chinese-red to-chinese-pink p-8 text-center">
            <DoubleHappiness size={80} color="white" />
            <h1 className="text-3xl md:text-4xl font-chinese font-bold text-white mt-4">
              婚礼回复 Wedding RSVP
            </h1>
            <p className="text-white/90 mt-2">
              请填写以下信息 Please fill in the information below
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Attendance */}
            <div className="text-center mb-8">
              <label className="block text-lg font-chinese font-bold text-gray-700 mb-4">
                您是否能够出席？Will you be attending?
              </label>
              <div className="flex justify-center gap-4">
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    name="attending"
                    value="true"
                    checked={formData.attending === true}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`px-8 py-3 rounded-full border-2 transition-all ${
                    formData.attending ? 'bg-chinese-red text-white border-chinese-red' : 'bg-white text-gray-700 border-gray-300'
                  }`}>
                    <span className="text-xl mr-2">✓</span>
                    欣然赴约 Joyfully Accept
                  </div>
                </label>
                <label className="cursor-pointer">
                  <input
                    type="radio"
                    name="attending"
                    value="false"
                    checked={formData.attending === false}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <div className={`px-8 py-3 rounded-full border-2 transition-all ${
                    !formData.attending ? 'bg-gray-500 text-white border-gray-500' : 'bg-white text-gray-700 border-gray-300'
                  }`}>
                    <span className="text-xl mr-2">✗</span>
                    遗憾缺席 Regretfully Decline
                  </div>
                </label>
              </div>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                姓名 Name <span className="text-chinese-red">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent transition-all"
                placeholder="请输入您的姓名 Please enter your name"
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                手机号码 Phone Number (选填 Optional)
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent transition-all"
                placeholder="请输入手机号 Please enter phone number"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                电子邮箱 Email (选填 Optional)
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent transition-all"
                placeholder="your@email.com"
              />
            </div>

            {/* Number of Guests - Only show if attending */}
            {formData.attending && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    出席人数 Number of Guests <span className="text-chinese-red">*</span>
                  </label>
                  <select
                    name="guests"
                    value={formData.guests}
                    onChange={handleChange}
                    required={formData.attending}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent transition-all"
                  >
                    {[1, 2, 3, 4, 5].map(num => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? '位 person' : '位 people'}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Dietary Restrictions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    饮食禁忌 Dietary Restrictions (选填 Optional)
                  </label>
                  <input
                    type="text"
                    name="dietary_restrictions"
                    value={formData.dietary_restrictions}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent transition-all"
                    placeholder="如：素食、清真、过敏等 e.g., Vegetarian, Halal, Allergies"
                  />
                </div>
              </>
            )}

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                祝福留言 Message (选填 Optional)
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-chinese-red focus:border-transparent transition-all resize-none"
                placeholder="写下您的祝福... Share your wishes..."
              />
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <button
                type="submit"
                disabled={loading}
                className={`w-full py-4 px-6 rounded-lg font-chinese font-bold text-white text-lg transition-all transform hover:scale-105 ${
                  loading 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-gradient-to-r from-chinese-red to-chinese-pink hover:shadow-lg'
                }`}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    提交中... Submitting...
                  </span>
                ) : (
                  <>
                    <span className="mr-2">💌</span>
                    提交回复 Submit RSVP
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Privacy Notice */}
        <div className="mt-6 text-center text-sm text-gray-600">
          <p>您的信息将仅用于婚礼安排 Your information will only be used for wedding arrangements</p>
        </div>
      </div>
    </div>
  );
};

export default RSVPForm;


